// SupplierDupCandidates.jsx — the assisted duplicate review queue (spec §7).
// Read-only proposer: embedding+trigram-ranked pairs of LIVE masters.
// Merging is explicit and directional: the chooser makes the user pick which
// supplier is KEPT (becomes the payee) and shows exactly what moves before the
// confirm. Never auto-merges (permanent policy).
import { useEffect, useState } from "react";
import { Button, Collapse } from "react-bootstrap";
import useFetchData from "./useFetchDatav2";
import { useToast } from "../Context/useToast";

const strengthClass = (cosine) =>
   cosine >= 0.97 ? "strength-high" : cosine >= 0.94 ? "strength-med" : "strength-low";

const PAIRS_PAGE_SIZE = 10;

export default function SupplierDupCandidates({ onReview, onChanged, refreshTick = 0 }) {
   const { fetchData } = useFetchData();
   const { triggerToast } = useToast();
   const [open, setOpen] = useState(false);
   const [pairs, setPairs] = useState([]);
   const [isLoading, setIsLoading] = useState(false);
   const [chooser, setChooser] = useState(null); // { pair, keepId } — keepId null until the user picks a side
   const [sideStats, setSideStats] = useState({}); // ext_id -> { aliases, templates } | "loading"
   const [isWorking, setIsWorking] = useState(false);
   const [reloadTick, setReloadTick] = useState(0);
   const [visibleCount, setVisibleCount] = useState(PAIRS_PAGE_SIZE);

   useEffect(() => {
      if (!open) return;
      let cancelled = false;
      (async () => {
         setIsLoading(true);
         try {
            const { responseData } = await fetchData("/getsupplierdupcandidates.sz", "POST", new FormData(), "form-data");
            if (!cancelled) {
               setPairs(responseData?.st === 1 && Array.isArray(responseData?.payload) ? responseData.payload : []);
               setVisibleCount(PAIRS_PAGE_SIZE);
            }
         } catch {
            if (!cancelled) setPairs([]);
         } finally {
            if (!cancelled) setIsLoading(false);
         }
      })();
      return () => { cancelled = true; };
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [open, refreshTick, reloadTick]);

   // Decision support: when the chooser opens, pull each side's alias/template
   // counts so "keep" is an informed choice, not a coin flip.
   const loadSideStats = async (extId) => {
      setSideStats((prev) => ({ ...prev, [extId]: "loading" }));
      try {
         const formData = new FormData();
         formData.append("_supplier_ext_id", extId);
         const { responseData } = await fetchData("/getsupplieraliases.sz", "POST", formData, "form-data");
         const detail = responseData?.st === 1 ? responseData?.payload?.[0] : null;
         setSideStats((prev) => ({
            ...prev,
            [extId]: {
               aliases: Array.isArray(detail?.aliases) ? detail.aliases.length : null,
               templates: Array.isArray(detail?.templates) ? detail.templates.length : null,
            },
         }));
      } catch {
         setSideStats((prev) => ({ ...prev, [extId]: { aliases: null, templates: null } }));
      }
   };

   const openChooser = (pair) => {
      setChooser({ pair, keepId: null });
      loadSideStats(pair.a_ext_id);
      loadSideStats(pair.b_ext_id);
   };

   const runMerge = async () => {
      const { pair, keepId } = chooser || {};
      if (!pair || keepId == null) return;
      const keep = keepId === pair.a_id
         ? { id: pair.a_id, name: pair.a_name }
         : { id: pair.b_id, name: pair.b_name };
      const retire = keepId === pair.a_id
         ? { id: pair.b_id, name: pair.b_name }
         : { id: pair.a_id, name: pair.a_name };
      setIsWorking(true);
      try {
         const formData = new FormData();
         formData.append("_source_id", retire.id);
         formData.append("_payee_id", keep.id);
         formData.append("_surface", "dup_sweep");
         const { responseData } = await fetchData("/mergesuppliermasters.sz", "POST", formData, "form-data");
         const result = responseData?.payload?.[0];
         if (responseData?.st === 1 && result?.status !== "error") {
            triggerToast("success",
               `${retire.name} merged into ${keep.name}. Its names, templates and history moved.`,
               false, "Supplier merged", false);
            setReloadTick((t) => t + 1);
            onChanged?.();
         } else {
            triggerToast("warning", result?.reason || responseData?.mg || "Merge failed.", false, "", false);
         }
      } catch {
         triggerToast("error", "We seem to have an issue accessing your data, please try again later.", true, "Network Issue", false, 0);
      } finally {
         setIsWorking(false);
         setChooser(null);
      }
   };

   const statsLine = (extId) => {
      const s = sideStats[extId];
      if (!s || s === "loading") return "…";
      const names = s.aliases == null ? "?" : s.aliases;
      const templates = s.templates == null ? "?" : s.templates;
      return `${names} known name${names === 1 ? "" : "s"} · ${templates} template${templates === 1 ? "" : "s"}`;
   };

   const renderSide = (pair, side) => {
      const id = side === "a" ? pair.a_id : pair.b_id;
      const extId = side === "a" ? pair.a_ext_id : pair.b_ext_id;
      const name = side === "a" ? pair.a_name : pair.b_name;
      const isKept = chooser?.keepId === id;
      const isRetiring = chooser?.keepId != null && !isKept;
      return (
         <button
            type="button"
            className={`merge-side ${isKept ? "selected" : ""} ${isRetiring ? "retiring" : ""}`}
            onClick={() => setChooser((prev) => (prev ? { ...prev, keepId: id } : prev))}
            disabled={isWorking}
         >
            <div className="merge-side-role">
               {isKept ? "Keep — receives everything" : isRetiring ? "Retires — everything moves out" : "Select to keep"}
            </div>
            <div className="merge-side-name">{name}</div>
            <div className="merge-side-stats">{statsLine(extId)}</div>
         </button>
      );
   };

   const chooserKeep = chooser?.keepId == null ? null
      : chooser.keepId === chooser.pair.a_id
         ? { name: chooser.pair.a_name }
         : { name: chooser.pair.b_name };
   const chooserRetire = chooser?.keepId == null ? null
      : chooser.keepId === chooser.pair.a_id
         ? { name: chooser.pair.b_name, extId: chooser.pair.b_ext_id }
         : { name: chooser.pair.a_name, extId: chooser.pair.a_ext_id };

   return (
      <div className="dup-queue">
         <div className="dup-queue-header">
            <span className="dup-queue-title">
               Possible duplicates
               {open && !isLoading && <span className="dup-count">{pairs.length}</span>}
               <span className="dup-queue-hint d-none d-md-inline">
                  ranked by name similarity — nothing merges automatically
               </span>
            </span>
            <Button variant={open ? "outline-secondary" : "primary"} size="sm" onClick={() => setOpen((o) => !o)}>
               {open ? "Hide" : "Review…"}
            </Button>
         </div>
         <Collapse in={open}>
            <div>
               <div className="dup-queue-body">
                  {isLoading ? (
                     <>
                        {[0, 1, 2].map((i) => (
                           <div key={`sk-${i}`} className="dup-skeleton">
                              <span style={{ width: "70%" }} />
                              <span />
                              <span style={{ width: "60%", justifySelf: "end" }} />
                              <span />
                           </div>
                        ))}
                     </>
                  ) : pairs.length === 0 ? (
                     <div className="dup-empty">
                        <span className="dup-empty-check">✓</span>
                        No likely duplicates among your live suppliers right now.
                     </div>
                  ) : (
                     pairs.slice(0, visibleCount).map((p) => {
                        const pct = Math.round((p.cosine ?? 0) * 100);
                        return (
                           <div key={`dup-${p.a_id}-${p.b_id}`} className="dup-pair">
                              <button type="button" className="dup-name" title={`Open ${p.a_name}`}
                                      onClick={() => onReview({ supplierId: p.a_ext_id, name: p.a_name })}>
                                 {p.a_name}
                              </button>
                              <div className={`dup-similarity ${strengthClass(p.cosine ?? 0)}`}>
                                 <div className="dup-similarity-value">{pct}% match</div>
                                 <div className="dup-similarity-meter">
                                    <span style={{ width: `${Math.max(8, (pct - 60) * 2.5)}%` }} />
                                 </div>
                              </div>
                              <button type="button" className="dup-name" title={`Open ${p.b_name}`}
                                      onClick={() => onReview({ supplierId: p.b_ext_id, name: p.b_name })}>
                                 {p.b_name}
                              </button>
                              <Button variant="outline-secondary" size="sm" onClick={() => openChooser(p)}>
                                 Merge…
                              </Button>
                           </div>
                        );
                     })
                  )}
                  {!isLoading && pairs.length > visibleCount && (
                     <div className="dup-show-more">
                        <span className="dup-show-more-count">
                           Showing {visibleCount} of {pairs.length}
                        </span>
                        <button type="button" className="btn btn-dark btn-sm"
                                onClick={() => setVisibleCount((c) => c + PAIRS_PAGE_SIZE)}>
                           Show more
                        </button>
                     </div>
                  )}
               </div>
            </div>
         </Collapse>

         {chooser && (
            <div className="modal d-block" tabIndex={-1} style={{ background: "rgba(0,0,0,0.4)", zIndex: 1070 }}>
               <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 560 }}>
                  <div className="modal-content merge-chooser">
                     <div className="modal-header py-2">
                        <div>
                           <div className="fw-semibold">Merge duplicate suppliers</div>
                           <div className="text-secondary" style={{ fontSize: "0.78rem" }}>
                              These look like the same payee. Choose which record to keep.
                           </div>
                        </div>
                        <button type="button" className="btn-close" onClick={() => setChooser(null)} disabled={isWorking} aria-label="Close" />
                     </div>
                     <div className="modal-body">
                        <div className="merge-chooser-sides">
                           {renderSide(chooser.pair, "a")}
                           {renderSide(chooser.pair, "b")}
                        </div>
                        {chooserKeep ? (
                           <div className="merge-effect">
                              Everything recorded under <strong>{chooserRetire.name}</strong> ({statsLine(chooserRetire.extId)},
                              plus its statements and ERP history) moves to <strong>{chooserKeep.name}</strong>.{" "}
                              {chooserRetire.name} is retired — nothing is deleted, and a name can be moved back later.
                           </div>
                        ) : (
                           <div className="merge-effect">
                              The kept supplier becomes the payee. The other one hands over its names,
                              templates, statements and ERP history, then retires. Nothing is deleted.
                           </div>
                        )}
                     </div>
                     <div className="modal-footer py-2">
                        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setChooser(null)} disabled={isWorking}>
                           Cancel
                        </button>
                        <button type="button" className="btn btn-primary btn-sm" onClick={runMerge} disabled={isWorking || chooser.keepId == null}>
                           {isWorking ? "Merging…" : chooserKeep ? `Merge into ${chooserKeep.name}` : "Choose which to keep"}
                        </button>
                     </div>
                  </div>
               </div>
            </div>
         )}
      </div>
   );
}
